import { createContext, createElement, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import type { TableColumn } from './viewTypes'
import { EMPTY_COLUMNS, isCustomisedLayout, resolveColumns, serializeLayout, type StoredColumnLayout } from './columnLayout'
import { toast } from '../toast/toastStore'

// AB#3954 — the persistence seam for table column layouts. @marque/ui never talks to an API: the
// tenant host supplies a store over AB#3962's per-user preference endpoints (kind `table-layout`,
// key = the table identity), the designer preview supplies none and the layout lives in memory for
// the life of the component. The hook below is the ONE consumer, so every primitive that renders a
// table personalises identically.

export interface TableLayoutStore {
    /** The user's stored layout for the table, or null when none is stored. Rejections fall back to defaults. */
    load(identity: string): Promise<StoredColumnLayout | null>
    /** Persist the layout. Called in the background after each change; a rejection keeps the local state and toasts. */
    save(identity: string, layout: StoredColumnLayout): Promise<void>
    /** Forget the stored layout (Reset to default). */
    reset(identity: string): Promise<void>
}

// The ambient store: mounted once by the tenant app so hosts that render ListView without threading
// props (GeneratedList's callers, custom slots) still persist. An explicit prop always wins.
const TableLayoutStoreContext = createContext<TableLayoutStore | undefined>(undefined)

export function TableLayoutStoreProvider({ store, children }: { store: TableLayoutStore | undefined; children: ReactNode }) {
    return createElement(TableLayoutStoreContext.Provider, { value: store }, children)
}

/** The store to use: the explicit prop when given, else the ambient provider's, else none (in-memory). */
export function useTableLayoutStore(explicit?: TableLayoutStore | undefined): TableLayoutStore | undefined {
    const ambient = useContext(TableLayoutStoreContext)
    return explicit ?? ambient
}

export interface TableLayoutState {
    /** The effective visible columns, in order. */
    columns: TableColumn[]
    /** Every column the table can show: defaults then available, declaration order. */
    allColumns: TableColumn[]
    /** True when `columns` differs from the module's defaults. */
    isCustomised: boolean
    /** Show a hidden column, at `atIndex` (clamped) or at the end. */
    show(field: string, atIndex?: number): void
    /** Hide a visible column. Never touches data — hidden is a display choice. */
    hide(field: string): void
    /** Move a visible column so it lands at `toIndex` among the remaining columns (clamped). */
    move(field: string, toIndex: number): void
    /** Back to the module's defaults; forgets the stored layout. */
    reset(): void
}

/** Save debounce: a run of quick changes (drag, drag, hide) persists once. */
export const TABLE_LAYOUT_SAVE_DEBOUNCE_MS = 400

/**
 * AB#3953 / AB#3954 — session-local column layout for one table. Loads from the store once per
 * identity (any failure = defaults, silently: a table must never fail to render over a preference),
 * applies every change locally at once, and saves in the background, debounced; a failed save keeps
 * the local state and reports through the shared toast. No identity or no store = in-memory only.
 */
export function useTableLayout(identity: string | undefined, defaults: readonly TableColumn[] = EMPTY_COLUMNS, available: readonly TableColumn[] = EMPTY_COLUMNS, store?: TableLayoutStore | undefined): TableLayoutState {
    const [stored, setStored] = useState<{ identity: string | undefined; layout: StoredColumnLayout | null }>({ identity, layout: null })
    // A layout loaded for a different identity is not this table's: read through only when it matches.
    const layout = stored.identity === identity ? stored.layout : null
    const columns = useMemo(() => resolveColumns(defaults, available, layout), [defaults, available, layout])
    const allColumns = useMemo(() => {
        const seen = new Set<string>()
        return [...defaults, ...available].filter(c => seen.has(c.field) ? false : (seen.add(c.field), true))
    }, [defaults, available])

    useEffect(() => {
        if (!identity || !store) { return }
        let cancelled = false
        store.load(identity)
            .then(loaded => { if (!cancelled) { setStored({ identity, layout: loaded }) } })
            .catch(() => { /* a preference that cannot be read must not break the table: defaults */ })
        return () => { cancelled = true }
    }, [identity, store])

    // Debounced background save. The pending write is flushed on unmount / identity change so a quick
    // navigation away does not lose the last change.
    const pending = useRef<{ identity: string; layout: StoredColumnLayout; timer: ReturnType<typeof setTimeout> } | null>(null)
    const persist = useCallback((id: string, next: StoredColumnLayout) => {
        if (!store) { return }
        store.save(id, next).catch((error: unknown) => {
            toast.error(`Your column layout could not be saved${error instanceof Error && error.message ? `: ${error.message}` : ''}. It stays as shown for now.`)
        })
    }, [store])
    const schedule = useCallback((id: string, next: StoredColumnLayout) => {
        if (pending.current) { clearTimeout(pending.current.timer) }
        pending.current = { identity: id, layout: next, timer: setTimeout(() => { pending.current = null; persist(id, next) }, TABLE_LAYOUT_SAVE_DEBOUNCE_MS) }
    }, [persist])
    useEffect(() => () => {
        const p = pending.current
        if (p) { clearTimeout(p.timer); pending.current = null; persist(p.identity, p.layout) }
    }, [persist, identity])

    const commit = useCallback((visible: TableColumn[]) => {
        const next = serializeLayout(defaults, available, visible)
        setStored({ identity, layout: next })
        if (identity) { schedule(identity, next) }
    }, [defaults, available, identity, schedule])

    const show = useCallback((field: string, atIndex?: number) => {
        if (columns.some(c => c.field === field)) { return }
        const column = allColumns.find(c => c.field === field)
        if (!column) { return }
        const next = [...columns]
        const at = atIndex === undefined ? next.length : Math.max(0, Math.min(atIndex, next.length))
        next.splice(at, 0, column)
        commit(next)
    }, [columns, allColumns, commit])

    const hide = useCallback((field: string) => {
        if (!columns.some(c => c.field === field) || columns.length <= 1) { return }
        commit(columns.filter(c => c.field !== field))
    }, [columns, commit])

    const move = useCallback((field: string, toIndex: number) => {
        const from = columns.findIndex(c => c.field === field)
        if (from < 0) { return }
        const next = columns.filter(c => c.field !== field)
        const at = Math.max(0, Math.min(toIndex, next.length))
        next.splice(at, 0, columns[from]!)
        if (next.every((c, i) => c.field === columns[i]?.field)) { return }
        commit(next)
    }, [columns, commit])

    const reset = useCallback(() => {
        if (pending.current) { clearTimeout(pending.current.timer); pending.current = null }
        setStored({ identity, layout: null })
        if (identity && store) {
            store.reset(identity).catch((error: unknown) => {
                toast.error(`Your column layout could not be reset${error instanceof Error && error.message ? `: ${error.message}` : ''}.`)
            })
        }
    }, [identity, store])

    return useMemo(() => ({
        columns, allColumns, isCustomised: isCustomisedLayout(defaults, columns), show, hide, move, reset,
    }), [columns, allColumns, defaults, show, hide, move, reset])
}
